import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import pt from "@/messages/pt.json"
import en from "@/messages/en.json"

export type Locale = "pt" | "en"

const messages = { pt, en } as const

type Messages = typeof pt

type NestedKeyOf<T, Prefix extends string = ""> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? NestedKeyOf<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`
    }[keyof T & string]
  : never

export type TranslationKey = NestedKeyOf<Messages>

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[key]
    return undefined
  }, obj)
}

interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey, params?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextType | null>(null)

function getLangFromUrl(): Locale | null {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  const lang = params.get("lang")
  if (lang === "pt" || lang === "en") return lang
  return null
}

function detectLocale(serverLocale?: Locale): Locale {
  // 1. Server locale (authenticated user) always wins
  if (serverLocale) {
    if (typeof window !== "undefined") localStorage.setItem("instruo-locale", serverLocale)
    return serverLocale
  }

  // 2. ?lang URL param (unauthenticated pages from instruo.ai)
  const urlLang = getLangFromUrl()
  if (urlLang) {
    if (typeof window !== "undefined") localStorage.setItem("instruo-locale", urlLang)
    return urlLang
  }

  if (typeof window === "undefined") return "pt"

  // 3. localStorage (persisted from previous visit)
  const saved = localStorage.getItem("instruo-locale")
  if (saved === "en" || saved === "pt") return saved

  // 4. Browser language
  const browserLang = navigator.language || ""
  if (browserLang.startsWith("en")) return "en"

  // 5. Default to pt (Brazilian market)
  return "pt"
}

interface I18nProviderProps {
  initialLocale?: Locale
  children: ReactNode
}

export function I18nProvider({ initialLocale, children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale(initialLocale))

  // Sync when server locale changes (e.g., after login)
  useEffect(() => {
    if (initialLocale && initialLocale !== locale) {
      setLocaleState(initialLocale)
      localStorage.setItem("instruo-locale", initialLocale)
      document.documentElement.lang = initialLocale === "pt" ? "pt-BR" : "en"
    }
  }, [initialLocale])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem("instruo-locale", newLocale)
    document.documentElement.lang = newLocale === "pt" ? "pt-BR" : "en"
  }, [])

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      const value = getNestedValue(
        messages[locale] as Record<string, unknown>,
        key
      )
      if (typeof value !== "string") return key
      if (!params) return value
      return Object.entries(params).reduce<string>(
        (result, [k, v]) => result.replace(`{${k}}`, String(v)),
        value
      )
    },
    [locale]
  )

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) throw new Error("useI18n must be used within I18nProvider")
  return context
}
